import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface DailyFinancialData {
  cashTotal: number;
  paidBenefit: number;
  paidCapital: number;
  paidRevenue: number;
  unpaidBenefit: number;
  unpaidCapital: number;
  unpaidRevenue: number;
  paidUnstockedCapital: number;
  paidStockedCapitalByBenefit: number;
  paidUnstockedCapitalByBenefit: number;
  realUnpaidStockedCapital: number;
  realUnpaidUnstockedCapital: number;
  restOfUnpaidStockedCapital: number;
  restOfUnpaidUnstockedCapital: number;
  unpaidUnstockedCapital: number;
  realUnpaidLoans: number;
  totalPaidLoan: number;
  rawPaidBenefit: number;
  totalUnpaidBrut: number;
  remainingUnpaid: number;
  
  // Optional pre-avoire (raw) metrics
  rawPaidRevenue?: number;
  rawPaidCapital?: number;
  
  // Optional refunded (avoir) metrics
  refundedRevenue?: number;
  refundedCapital?: number;
  refundedBenefit?: number;
  totalAvoirAmount?: number;
}

export interface FinancialReportResponse {
  [date: string]: DailyFinancialData;
}

export interface AggregatedFinancialData {
  totalCash: number;
  totalPaidBenefit: number;
  totalPaidCapital: number;
  totalPaidRevenue: number;
  totalUnpaidBenefit: number;
  totalUnpaidCapital: number;
  totalUnpaidRevenue: number;
  totalPaidUnstockedCapital: number;
  totalPaidStockedCapitalByBenefit: number;
  totalPaidUnstockedCapitalByBenefit: number;
  totalUnpaidUnstockedCapital: number;
  totalRealUnpaidLoans: number;
  totalPaidLoan: number;
  totalPaidLoanByBenefit: number;
  totalRawPaidBenefit: number;
  totalUnpaidBrut: number;
  totalRemainingUnpaid: number;
  
  // Optional pre-avoire and refunded metrics
  totalRawPaidRevenue?: number;
  totalRawPaidCapital?: number;
  totalRefundedRevenue?: number;
  totalRefundedCapital?: number;
  totalRefundedBenefit?: number;
  totalAvoirAmount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class FinancialReportService {

  constructor(private http: HttpClient) { }

  // Get daily reports for trend charts and detailed tables
  getDailyReports(startDate: string, endDate: string): Observable<FinancialReportResponse> {
    const params = { startDate, endDate };
    const url = `${environment.apiUrl}/reports/daily`;
    console.log('🔍 [FINANCIAL-SERVICE] Calling daily reports API:', url, 'with params:', params);
    return this.http.get<FinancialReportResponse>(url, { params });
  }

  // Get aggregated reports for summary cards and distribution charts
  getAggregatedReports(startDate: string, endDate: string): Observable<AggregatedFinancialData> {
    const params = { startDate, endDate };
    const url = `${environment.apiUrl}/reports/aggregated`;
    console.log('🔍 [FINANCIAL-SERVICE] Calling aggregated reports API:', url, 'with params:', params);
    return this.http.get<AggregatedFinancialData>(url, { params });
  }

  getCurrentCash(): Observable<number> {
    const url = `${environment.apiUrl}/reports/current-cash`;
    return this.http.get<number>(url);
  }

  // Helper methods for data processing
  getSortedDates(data: FinancialReportResponse): string[] {
    return Object.keys(data).sort();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
}
